import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  setDoc,
  updateDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  Timestamp,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from './auth'
import { useSyncStore } from './sync'
import { writeAuditLog } from './auditLog'
import type { RadioLogEntry } from '@/types'

export const useRadioLogStore = defineStore('radioLog', () => {
  const entries = ref<RadioLogEntry[]>([])
  const isLoading = ref(false)

  let unsubscribe: Unsubscribe | null = null
  let _incidentId = ''
  let _opId = ''

  function subscribeToLog(incidentId: string, opId: string) {
    if (incidentId === _incidentId && opId === _opId && unsubscribe) return
    if (unsubscribe) unsubscribe()
    _incidentId = incidentId
    _opId = opId

    isLoading.value = true
    entries.value = []

    const q = query(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'radio_log'),
      orderBy('client_timestamp', 'desc'),
    )

    unsubscribe = onSnapshot(q, (snap) => {
      entries.value = snap.docs
        .map((d) => ({ entry_id: d.id, ...d.data() }) as RadioLogEntry)
        .filter((e) => e.deleted_at === null || e.deleted_at === undefined)
      isLoading.value = false
    })
  }

  function unsubscribeFromLog() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
  }

  async function addEntry(
    incidentId: string,
    opId: string,
    payload: {
      team_identifier: string
      team_id: string | null
      message_type: string
      assignment_id: string | null
      assignment_name: string | null
      priority_traffic_plan: string | null
      notes: string | null
    },
  ) {
    const authStore = useAuthStore()
    const sync = useSyncStore()
    const user = authStore.currentUser
    const profile = authStore.userProfile
    const operatorName = profile
      ? `${profile.user_first_name} ${profile.user_last_name}`.trim()
      : 'Unknown'

    const entryId = crypto.randomUUID()
    const clientTimestamp = Timestamp.now()

    const entryData = {
      client_timestamp: clientTimestamp,
      team_identifier: payload.team_identifier,
      team_id: payload.team_id,
      message_type: payload.message_type,
      assignment_id: payload.assignment_id,
      assignment_name: payload.assignment_name,
      priority_traffic_plan: payload.priority_traffic_plan,
      notes: payload.notes,
      operator_id: user?.uid ?? 'unknown',
      operator_name: operatorName,
      deleted_at: null,
    }

    sync.pendingWrites++
    try {
      await setDoc(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'radio_log', entryId),
        entryData,
      )
      await writeAuditLog(
        incidentId,
        'radio_log.entry_created',
        'radio_log_entry',
        entryId,
        null,
        {
          team_identifier: payload.team_identifier,
          message_type: payload.message_type,
          notes: payload.notes,
          client_timestamp: clientTimestamp.toMillis(),
        },
      )
    } finally {
      sync.pendingWrites--
    }
  }

  async function editEntry(
    incidentId: string,
    opId: string,
    entryId: string,
    payload: {
      team_identifier: string
      team_id: string | null
      message_type: string
      assignment_id: string | null
      assignment_name: string | null
      priority_traffic_plan: string | null
      notes: string | null
    },
    before: Pick<RadioLogEntry, 'team_identifier' | 'message_type' | 'notes'>,
  ) {
    const authStore = useAuthStore()
    const sync = useSyncStore()
    const profile = authStore.userProfile
    const editedByName = profile
      ? `${profile.user_first_name} ${profile.user_last_name}`.trim()
      : 'Unknown'
    const editedAt = Timestamp.now()

    sync.pendingWrites++
    try {
      await updateDoc(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'radio_log', entryId),
        { ...payload, edited_at: editedAt, edited_by_name: editedByName },
      )
      await writeAuditLog(
        incidentId,
        'radio_log.entry_edited',
        'radio_log_entry',
        entryId,
        { team_identifier: before.team_identifier, message_type: before.message_type, notes: before.notes },
        { team_identifier: payload.team_identifier, message_type: payload.message_type, notes: payload.notes },
      )
    } finally {
      sync.pendingWrites--
    }
  }

  async function deleteEntry(incidentId: string, opId: string, entryId: string) {
    const sync = useSyncStore()
    const deletedAt = Timestamp.now()

    sync.pendingWrites++
    try {
      await updateDoc(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'radio_log', entryId),
        { deleted_at: deletedAt },
      )
      await writeAuditLog(
        incidentId,
        'radio_log.entry_deleted',
        'radio_log_entry',
        entryId,
        { deleted_at: null },
        { deleted_at: deletedAt.toMillis() },
      )
    } finally {
      sync.pendingWrites--
    }
  }

  function teardown() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    _incidentId = ''
    _opId = ''
    entries.value = []
    isLoading.value = false
  }

  return {
    entries,
    isLoading,
    subscribeToLog,
    unsubscribeFromLog,
    addEntry,
    editEntry,
    deleteEntry,
    teardown,
  }
})
